// The missed step's flashcard deck, pure (Leah's instructions, "Flashcards":
// before the paragraphs, a deck of the key facts the learner missed; the
// question on the front; Got it or Not yet, Not yet to the back of the pile;
// the step ends when every card is Got it; the whole deck when the revision
// was skipped). A card is identified by its screen and its place on it, so a
// deck survives a reload and re-deals only when the missed facts change.

import type { WorkshopBlock } from '../types';
import { seededOrder } from '../scoring';
import { recallKeyFacts } from './steps';

/** One key fact a learner missed: `caseStudy:page:index`. */
export type CardId = string;

export const cardId = (caseStudy: string, page: number, index: number): CardId => `${caseStudy}:${page}:${index}`;

export interface Card {
  id: CardId;
  /** The recall screen's name, shown above the question. */
  group: string;
  /** The flashcard question. */
  cue: string;
  /** The key fact, the answer. */
  answer: string;
}

export interface MissedScreen {
  caseStudy: string;
  page: number;
  /** Indices of the key facts not ticked. */
  missed: number[];
}

/**
 * The deck: every key fact missed on a saved recall screen of the chosen
 * case studies. When nothing was recalled because the revision was skipped,
 * every key fact of those case studies.
 */
export function deckIds(w: WorkshopBlock, chosen: string[], missed: MissedScreen[], skipped: boolean, anyRecalled: boolean): CardId[] {
  const out: CardId[] = [];
  for (const m of missed) for (const j of m.missed) out.push(cardId(m.caseStudy, m.page, j));
  if (out.length === 0 && skipped && !anyRecalled) {
    for (const k of chosen) {
      const e = w.editions[k];
      if (!e) continue;
      e.revision.forEach((p, i) => {
        if (!p.recall) return;
        recallKeyFacts(e, p).forEach((_, j) => out.push(cardId(k, i, j)));
      });
    }
  }
  return out;
}

/** The deck's signature: when it changes, the pile is dealt again. */
export const deckSignature = (ids: CardId[]): string => ids.join('|');

/** A fresh pile, shuffled the same way for the same deck and learner selection. */
export const dealDeck = (ids: CardId[], seed: string): CardId[] => seededOrder(ids.length, `${seed}|${deckSignature(ids)}`).map((i) => ids[i]);

export function cardOf(w: WorkshopBlock, id: CardId): Card | null {
  const [caseStudy, pageStr, indexStr] = id.split(':');
  const e = w.editions[caseStudy];
  const page = e?.revision[Number(pageStr)];
  if (!e || !page?.recall) return null;
  const facts = recallKeyFacts(e, page);
  const k = facts[Number(indexStr)];
  if (!k) return null;
  return { id, group: page.recall.name, cue: k.cue, answer: k.text };
}
